import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useAuthenticationStore } from '@/features/auth/stores/authenticationStore.js';
import { useOrganizationStore } from '@/features/organizations/stores/organizationStore.js';
import { useClient } from '@/plugins/api.js';

const workspaceSelectionStoragePrefix = 'socialize.workspace-selection';

export const useWorkspaceStore = defineStore('workspace', () => {
    const auth = useAuthenticationStore();
    const organizationStore = useOrganizationStore();
    const client = useClient();

    const workspaces = ref([]);
    const activeWorkspaceId = ref(null);
    const isLoading = ref(false);
    const isCreating = ref(false);
    const isUpdating = ref(false);
    const isDeleting = ref(false);
    const isUploadingLogo = ref(false);
    const invitesByWorkspace = ref({});
    const membersByWorkspace = ref({});
    const isInvitesLoading = ref(false);
    const isMembersLoading = ref(false);
    const isUpdatingMember = ref(false);
    const isRemovingMember = ref(false);
    const isInviting = ref(false);
    const isAcceptingInvite = ref(false);
    const isCancellingInvite = ref(false);
    const isResendingInvite = ref(false);
    const error = ref(null);

    const activeWorkspace = computed(
        () => workspaces.value.find(workspace => workspace.id === activeWorkspaceId.value) ?? null
    );

    function workspaceSelectionStorageKey() {
        return auth.userId ? `${workspaceSelectionStoragePrefix}.${auth.userId}` : null;
    }

    function readStoredWorkspaceSelection() {
        const storageKey = workspaceSelectionStorageKey();
        if (!storageKey || typeof window === 'undefined') {
            return null;
        }

        try {
            const storedValue = window.localStorage.getItem(storageKey);
            if (!storedValue) {
                return null;
            }

            const parsedValue = JSON.parse(storedValue);
            return {
                activeWorkspaceId:
                    typeof parsedValue.activeWorkspaceId === 'string' ? parsedValue.activeWorkspaceId : null,
            };
        } catch (storageError) {
            console.error('Failed to read stored workspace selection:', storageError);
            return null;
        }
    }

    function persistWorkspaceSelection() {
        const storageKey = workspaceSelectionStorageKey();
        if (!storageKey || typeof window === 'undefined') {
            return;
        }

        try {
            window.localStorage.setItem(
                storageKey,
                JSON.stringify({
                    activeWorkspaceId: activeWorkspaceId.value,
                })
            );
        } catch (storageError) {
            console.error('Failed to persist workspace selection:', storageError);
        }
    }

    function restoreWorkspaceSelection() {
        const storedSelection = readStoredWorkspaceSelection();
        if (
            storedSelection?.activeWorkspaceId &&
            workspaces.value.some(workspace => workspace.id === storedSelection.activeWorkspaceId)
        ) {
            activeWorkspaceId.value = storedSelection.activeWorkspaceId;
            return;
        }

        if (workspaces.value.some(workspace => workspace.id === activeWorkspaceId.value)) {
            return;
        }

        const selectedOrganizationWorkspace = organizationStore.selectedOrganizationId
            ? workspaces.value.find(workspace => workspace.organizationId === organizationStore.selectedOrganizationId)
            : null;

        activeWorkspaceId.value = selectedOrganizationWorkspace?.id ?? workspaces.value[0]?.id ?? null;
    }

    async function fetchWorkspaces() {
        if (!auth.isAuthenticated) {
            workspaces.value = [];
            activeWorkspaceId.value = null;
            error.value = null;
            return;
        }

        isLoading.value = true;
        error.value = null;

        try {
            const response = await client.get('/api/workspaces');
            workspaces.value = response.data ?? [];

            restoreWorkspaceSelection();

            organizationStore.setSelectedOrganizationFromWorkspace(activeWorkspace.value);
            persistWorkspaceSelection();
        } catch (fetchError) {
            console.error('Failed to fetch workspaces:', fetchError);
            workspaces.value = [];
            activeWorkspaceId.value = null;
            error.value = 'Failed to load workspaces.';
        } finally {
            isLoading.value = false;
        }
    }

    async function createWorkspace(payload) {
        if (!auth.isAuthenticated) {
            throw new Error('You must be authenticated to create a workspace.');
        }

        if (isCreating.value) {
            throw new Error('A workspace creation request is already in progress.');
        }

        isCreating.value = true;
        error.value = null;

        try {
            const response = await client.post('/api/workspaces', payload);

            if (response.data) {
                workspaces.value = [...workspaces.value, response.data].sort((left, right) =>
                    left.name.localeCompare(right.name)
                );
                activeWorkspaceId.value = response.data.id;
                persistWorkspaceSelection();
            }

            return response.data;
        } catch (createError) {
            console.error('Failed to create workspace:', createError);
            error.value = 'Failed to create workspace.';
            throw createError;
        } finally {
            isCreating.value = false;
        }
    }

    async function updateWorkspace(workspaceId, payload) {
        if (!auth.isAuthenticated || !workspaceId) {
            throw new Error('You must be authenticated to update a workspace.');
        }

        if (isUpdating.value) {
            throw new Error('A workspace update request is already in progress.');
        }

        isUpdating.value = true;
        error.value = null;

        try {
            const response = await client.put(`/api/workspaces/${workspaceId}`, payload);

            if (response.data) {
                workspaces.value = workspaces.value
                    .map(workspace => (workspace.id === workspaceId ? response.data : workspace))
                    .sort((left, right) => left.name.localeCompare(right.name));
            }

            return response.data;
        } catch (updateError) {
            console.error('Failed to update workspace:', updateError);
            error.value = 'Failed to update workspace.';
            throw updateError;
        } finally {
            isUpdating.value = false;
        }
    }

    async function deleteWorkspace(workspaceId, confirmationName) {
        if (!auth.isAuthenticated || !workspaceId) {
            throw new Error('You must be authenticated to delete a workspace.');
        }

        if (isDeleting.value) {
            throw new Error('A workspace deletion request is already in progress.');
        }

        isDeleting.value = true;
        error.value = null;

        try {
            await client.delete(`/api/workspaces/${workspaceId}`, {
                data: {
                    confirmationName,
                },
            });

            workspaces.value = workspaces.value.filter(workspace => workspace.id !== workspaceId);

            if (activeWorkspaceId.value === workspaceId) {
                activeWorkspaceId.value = workspaces.value[0]?.id ?? null;
            }

            organizationStore.setSelectedOrganizationFromWorkspace(activeWorkspace.value);
            persistWorkspaceSelection();
        } catch (deleteError) {
            console.error('Failed to delete workspace:', deleteError);
            error.value = 'Failed to delete workspace.';
            throw deleteError;
        } finally {
            isDeleting.value = false;
        }
    }

    async function uploadWorkspaceLogo(workspaceId, file) {
        if (!auth.isAuthenticated || !workspaceId) {
            throw new Error('You must be authenticated to upload a workspace logo.');
        }

        if (isUploadingLogo.value) {
            throw new Error('A workspace logo upload is already in progress.');
        }

        isUploadingLogo.value = true;
        error.value = null;

        try {
            const formData = new FormData();
            formData.append('file', file, file.name || 'workspace-logo.png');

            const response = await client.post(`/api/workspaces/${workspaceId}/logo`, formData);
            const blobUrl = response.data?.blobUrl;

            if (blobUrl) {
                workspaces.value = workspaces.value.map(workspace =>
                    workspace.id === workspaceId ? { ...workspace, logoUrl: `${blobUrl}?${Date.now()}` } : workspace
                );
            }

            return response.data;
        } catch (uploadError) {
            console.error('Failed to upload workspace logo:', uploadError);
            error.value = 'Failed to upload workspace logo.';
            throw uploadError;
        } finally {
            isUploadingLogo.value = false;
        }
    }

    function setActiveWorkspace(workspaceId) {
        if (!workspaceId) {
            activeWorkspaceId.value = workspaces.value[0]?.id ?? null;
            organizationStore.setSelectedOrganizationFromWorkspace(activeWorkspace.value);
            persistWorkspaceSelection();
            return;
        }

        if (workspaces.value.some(workspace => workspace.id === workspaceId)) {
            activeWorkspaceId.value = workspaceId;
            organizationStore.setSelectedOrganizationFromWorkspace(activeWorkspace.value);
            persistWorkspaceSelection();
        }
    }

    function setOrganizationWorkspace(organizationId) {
        const organizationWorkspaces = workspaces.value.filter(
            workspace => workspace.organizationId === organizationId
        );

        if (organizationWorkspaces.length === 0) {
            return;
        }

        if (organizationWorkspaces.some(workspace => workspace.id === activeWorkspaceId.value)) {
            organizationStore.setSelectedOrganization(organizationId);
            persistWorkspaceSelection();
            return;
        }

        activeWorkspaceId.value = organizationWorkspaces[0].id;
        organizationStore.setSelectedOrganization(organizationId);
        persistWorkspaceSelection();
    }

    async function fetchInvites(workspaceId = activeWorkspaceId.value) {
        if (!auth.isAuthenticated || !workspaceId) {
            invitesByWorkspace.value = {};
            return [];
        }

        isInvitesLoading.value = true;

        try {
            const response = await client.get(`/api/workspaces/${workspaceId}/invites`);
            invitesByWorkspace.value = {
                ...invitesByWorkspace.value,
                [workspaceId]: response.data ?? [],
            };

            return invitesByWorkspace.value[workspaceId];
        } catch (fetchError) {
            console.error('Failed to fetch workspace invites:', fetchError);
            throw fetchError;
        } finally {
            isInvitesLoading.value = false;
        }
    }

    async function fetchMembers(workspaceId = activeWorkspaceId.value) {
        if (!auth.isAuthenticated || !workspaceId) {
            membersByWorkspace.value = {};
            return [];
        }

        isMembersLoading.value = true;

        try {
            const response = await client.get(`/api/workspaces/${workspaceId}/members`);
            membersByWorkspace.value = {
                ...membersByWorkspace.value,
                [workspaceId]: response.data ?? [],
            };

            return membersByWorkspace.value[workspaceId];
        } catch (fetchError) {
            console.error('Failed to fetch workspace members:', fetchError);
            throw fetchError;
        } finally {
            isMembersLoading.value = false;
        }
    }

    async function inviteMember(payload) {
        if (!auth.isAuthenticated || !activeWorkspaceId.value) {
            throw new Error('You must be authenticated to invite a workspace member.');
        }

        if (isInviting.value) {
            throw new Error('A workspace invite request is already in progress.');
        }

        isInviting.value = true;

        try {
            const response = await client.post(`/api/workspaces/${activeWorkspaceId.value}/invites`, payload);
            invitesByWorkspace.value = {
                ...invitesByWorkspace.value,
                [activeWorkspaceId.value]: [
                    response.data,
                    ...(invitesByWorkspace.value[activeWorkspaceId.value] ?? []),
                ],
            };

            return response.data;
        } catch (inviteError) {
            console.error('Failed to create workspace invite:', inviteError);
            throw inviteError;
        } finally {
            isInviting.value = false;
        }
    }

    async function updateMember(workspaceId, userId, payload) {
        if (!auth.isAuthenticated || !workspaceId || !userId) {
            throw new Error('You must be authenticated to update a workspace member.');
        }

        isUpdatingMember.value = true;

        try {
            const response = await client.patch(`/api/workspaces/${workspaceId}/members/${userId}`, payload);
            if (response.data) {
                membersByWorkspace.value = {
                    ...membersByWorkspace.value,
                    [workspaceId]: (membersByWorkspace.value[workspaceId] ?? []).map(member =>
                        member.id === userId ? response.data : member
                    ),
                };
            }

            return response.data;
        } catch (updateError) {
            console.error('Failed to update workspace member:', updateError);
            throw updateError;
        } finally {
            isUpdatingMember.value = false;
        }
    }

    async function removeMember(workspaceId, userId) {
        if (!auth.isAuthenticated || !workspaceId || !userId) {
            throw new Error('You must be authenticated to remove a workspace member.');
        }

        isRemovingMember.value = true;

        try {
            await client.delete(`/api/workspaces/${workspaceId}/members/${userId}`);
            membersByWorkspace.value = {
                ...membersByWorkspace.value,
                [workspaceId]: (membersByWorkspace.value[workspaceId] ?? []).filter(member => member.id !== userId),
            };
        } catch (removeError) {
            console.error('Failed to remove workspace member:', removeError);
            throw removeError;
        } finally {
            isRemovingMember.value = false;
        }
    }

    async function cancelInvite(inviteId) {
        if (!auth.isAuthenticated || !inviteId) {
            throw new Error('You must be authenticated to cancel a workspace invite.');
        }

        if (isCancellingInvite.value) {
            throw new Error('A workspace invite cancellation request is already in progress.');
        }

        isCancellingInvite.value = true;

        try {
            const response = await client.post(`/api/workspace-invites/${inviteId}/cancel`);
            const cancelledInvite = response.data;
            const workspaceId = cancelledInvite?.workspaceId ?? activeWorkspaceId.value;

            if (workspaceId) {
                invitesByWorkspace.value = {
                    ...invitesByWorkspace.value,
                    [workspaceId]: (invitesByWorkspace.value[workspaceId] ?? []).filter(
                        invite => invite.id !== inviteId
                    ),
                };
            }

            return cancelledInvite;
        } catch (cancelError) {
            console.error('Failed to cancel workspace invite:', cancelError);
            throw cancelError;
        } finally {
            isCancellingInvite.value = false;
        }
    }

    async function resendInvite(inviteId) {
        if (!auth.isAuthenticated || !inviteId) {
            throw new Error('You must be authenticated to resend a workspace invite.');
        }

        if (isResendingInvite.value) {
            throw new Error('A workspace invite resend request is already in progress.');
        }

        isResendingInvite.value = true;

        try {
            const response = await client.post(`/api/workspace-invites/${inviteId}/resend`);
            return response.data;
        } catch (resendError) {
            console.error('Failed to resend workspace invite:', resendError);
            throw resendError;
        } finally {
            isResendingInvite.value = false;
        }
    }

    async function resolveInvite(inviteId) {
        if (!inviteId) {
            throw new Error('An invite id is required.');
        }

        const response = await client.get(`/api/workspace-invites/accept/${inviteId}`);
        return response.data;
    }

    async function acceptInvite(inviteId) {
        if (!auth.isAuthenticated || !inviteId) {
            throw new Error('You must be authenticated to accept a workspace invite.');
        }

        if (isAcceptingInvite.value) {
            throw new Error('A workspace invite acceptance request is already in progress.');
        }

        isAcceptingInvite.value = true;

        try {
            const response = await client.post(`/api/workspace-invites/${inviteId}/accept`);
            return response.data;
        } catch (acceptError) {
            console.error('Failed to accept workspace invite:', acceptError);
            throw acceptError;
        } finally {
            isAcceptingInvite.value = false;
        }
    }

    watch(
        () => auth.isAuthenticated,
        async isAuthenticated => {
            if (!isAuthenticated) {
                workspaces.value = [];
                activeWorkspaceId.value = null;
                error.value = null;
                return;
            }

            await fetchWorkspaces();
        },
        { immediate: true }
    );

    return {
        workspaces,
        activeWorkspaceId,
        activeWorkspace,
        isLoading,
        isCreating,
        isUpdating,
        isDeleting,
        isUploadingLogo,
        invitesByWorkspace,
        membersByWorkspace,
        isInvitesLoading,
        isMembersLoading,
        isUpdatingMember,
        isRemovingMember,
        isInviting,
        isAcceptingInvite,
        isCancellingInvite,
        isResendingInvite,
        error,
        fetchWorkspaces,
        createWorkspace,
        updateWorkspace,
        deleteWorkspace,
        uploadWorkspaceLogo,
        fetchInvites,
        fetchMembers,
        inviteMember,
        updateMember,
        removeMember,
        cancelInvite,
        resendInvite,
        resolveInvite,
        acceptInvite,
        setActiveWorkspace,
        setOrganizationWorkspace,
    };
});
